import { PokemonType } from "types/battle-engine/battle";
import { ItemCardTier } from "types/item-cards/item-card-definition";
import type { ItemCardDefinition } from "types/item-cards/item-card-definition";

export interface CardTheme {
  main: string;
  dark: string;
  light: string;
  tag: string;
}

export const TYPE_COLORS: Record<PokemonType, string> = {
  [PokemonType.NORMAL]: "#a8a878",
  [PokemonType.FIRE]: "#f08030",
  [PokemonType.WATER]: "#6890f0",
  [PokemonType.ELECTRIC]: "#f8d030",
  [PokemonType.GRASS]: "#78c850",
  [PokemonType.ICE]: "#98d8d8",
  [PokemonType.FIGHTING]: "#c03028",
  [PokemonType.POISON]: "#a040a0",
  [PokemonType.GROUND]: "#e0c068",
  [PokemonType.FLYING]: "#a890f0",
  [PokemonType.PSYCHIC]: "#f85888",
  [PokemonType.BUG]: "#a8b820",
  [PokemonType.ROCK]: "#b8a038",
  [PokemonType.GHOST]: "#705898",
  [PokemonType.DRAGON]: "#7038f8",
  [PokemonType.DARK]: "#705848",
  [PokemonType.STEEL]: "#b8b8d0",
  [PokemonType.FAIRY]: "#ee99ac",
};

/** Mix a hex color toward white (f > 0) or black (f < 0). */
export const shade = (hex: string, f: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(f > 0 ? c + (255 - c) * f : c * (1 + f));
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

/** Derive a full card theme from a single base color. */
export const themeFromColor = (color: string, tag: string): CardTheme => ({
  main: color,
  dark: shade(color, -0.78),
  light: shade(color, 0.45),
  tag,
});

const TIER_THEMES: Record<ItemCardTier, CardTheme> = {
  [ItemCardTier.FREE]: { main: "#649a54", dark: "#1c3320", light: "#acca82", tag: "FREE" },
  [ItemCardTier.SUSTAIN]: { main: "#4f6494", dark: "#1f2440", light: "#83a1ce", tag: "SUSTAIN" },
  [ItemCardTier.BUFF]: { main: "#c79234", dark: "#3a280f", light: "#f2d285", tag: "BUFF" },
  [ItemCardTier.TYPED]: { main: "#b1533c", dark: "#3c1512", light: "#f1a353", tag: "TYPED" },
};

/** Theme for an item card, tinted by its type when it has one. */
export const themeForItem = (def: ItemCardDefinition): CardTheme => {
  const base = TIER_THEMES[def.tier];
  if (!def.type) return base;
  return { ...base, main: TYPE_COLORS[def.type] ?? base.main, tag: def.type };
};

/** Theme derived from a pokemon's primary type. */
export const themeForPokemonTypes = (types: PokemonType[]): CardTheme =>
  themeFromColor(TYPE_COLORS[types[0]!] ?? "#a8a878", types[0]!);
